import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import * as scanner from '../../platform/scanner';
import { listSerials } from './api';

interface Props {
  /** Checked against what is already on the register, so a number that cannot
   *  be accepted is refused at the moment it is read rather than when the whole
   *  batch is submitted. */
  componentId?: string | null;
  /** How many units are expected, when that is known — a receipt says so. */
  expected?: number;
  /** Numbers already entered by hand or scanned earlier in this sitting. */
  initial?: string[];
  onDone: (serials: string[]) => void;
  onCancel: () => void;
}

type Note = { kind: 'duplicate' | 'registered' | 'added'; value: string } | null;

/**
 * Reading serial numbers off the units with the camera.
 *
 * The camera stays running between units: a parcel is unpacked one piece at a
 * time and stopping and restarting for each would be slower than typing. Each
 * distinct reading is appended and the count shown against what is expected, so
 * the answer to "have I done them all" is on screen rather than in the head.
 *
 * Everything can still be typed. The camera is a convenience, and a label that
 * has rubbed off is exactly the case where it fails.
 */
export function SerialScanner({ componentId, expected, initial = [], onDone, onCancel }: Props) {
  const { t } = useTranslation();

  const video = useRef<HTMLVideoElement>(null);
  const session = useRef<scanner.Session | null>(null);
  // Read inside the camera callback, which closes over its first render.
  const captured = useRef<string[]>(initial.filter(Boolean));

  const [values, setValues] = useState<string[]>(captured.current);
  const [note, setNote] = useState<Note>(null);
  const [error, setError] = useState<string | null>(null);
  const [live, setLive] = useState(false);
  const [typed, setTyped] = useState('');

  const support = scanner.cameraSupport();

  const add = async (value: string) => {
    const cleaned = value.trim();
    if (!cleaned) return;

    if (captured.current.some((existing) => existing.toUpperCase() === cleaned.toUpperCase())) {
      setNote({ kind: 'duplicate', value: cleaned });
      return;
    }

    captured.current = [...captured.current, cleaned];
    setValues(captured.current);
    setNote({ kind: 'added', value: cleaned });
    // A short buzz, because the eye is on the label and not on the screen.
    navigator.vibrate?.(40);

    if (!componentId) return;
    try {
      const found = await listSerials(componentId, undefined, cleaned);
      if (found.some((unit) => (unit.serial ?? '').toUpperCase() === cleaned.toUpperCase())) {
        // Said now rather than at submit: finding out that one number of twenty
        // was already on the register, after the twenty are scanned, is a worse
        // moment to find out.
        setNote({ kind: 'registered', value: cleaned });
      }
    } catch {
      // The check is a courtesy; the server refuses a real duplicate anyway.
    }
  };

  const startCamera = async () => {
    setError(null);
    if (!video.current) return;
    try {
      session.current = await scanner.start(video.current, (reading) => void add(reading.value));
      // In the application the reader has already closed by the time this
      // resolves, so there is nothing to offer to switch off. It also means one
      // press reads one unit, which is how serial numbers are entered anyway.
      setLive(!scanner.oneShot);
    } catch (exc) {
      setError(`scan.camera.${(exc as Error).message}`);
    }
  };

  const stop = () => {
    session.current?.stop();
    session.current = null;
    setLive(false);
  };

  // A stream left running keeps the indicator light on and the phone warm, and
  // looks to the user exactly like the application spying on them.
  useEffect(() => () => session.current?.stop(), []);

  const remove = (index: number) => {
    captured.current = captured.current.filter((_, position) => position !== index);
    setValues(captured.current);
  };

  const enough = expected === undefined || values.length === expected;

  // Rendered at the top of the document, not where it is written. The receiving
  // screen puts this inside its own form, and a form nested in a form is not
  // valid HTML: the browser discards the inner one, so pressing Enter on the
  // manual-entry field would submit the whole receipt instead of adding a
  // number. A portal also keeps the overlay out of any ancestor's stacking
  // context, which is the other way a fixed sheet quietly ends up clipped.
  return createPortal(
    <div className="serial-scan" role="dialog" aria-modal="true">
      <div className="serial-scan__box">
        <header className="serial-scan__head">
          <h3>{t('serials.scan.title')}</h3>
          <button type="button" onClick={onCancel}>
            {t('common.close')}
          </button>
        </header>

        <div className="scan__camera">
          {/* Kept mounted even when idle: starting the stream needs the element
              to exist already, and creating it on demand costs a black frame. */}
          <video ref={video} className={live ? 'scan__video is-live' : 'scan__video'} muted />
          {support === 'ok' ? (
            <button type="button" onClick={live ? stop : () => void startCamera()}>
              {live ? t('scan.stop') : t('scan.start')}
            </button>
          ) : (
            <p className="hint">{t(`scan.camera.${support}`)}</p>
          )}
          {error && <p className="hint">{t(error)}</p>}
        </div>

        <p className="serial-scan__count">
          {expected === undefined
            ? t('serials.scan.captured', { count: values.length })
            : t('serials.scan.progress', { entered: values.length, expected })}
        </p>

        {note && (
          <p className={note.kind === 'added' ? 'serial-scan__ok' : 'serial-scan__warn'}>
            {t(`serials.scan.${note.kind}`, { value: note.value })}
          </p>
        )}

        <ol className="serial-scan__list">
          {values.map((value, index) => (
            <li key={`${value}-${index}`}>
              <code>{value}</code>
              <button type="button" className="link" onClick={() => remove(index)}>
                {t('common.delete')}
              </button>
            </li>
          ))}
        </ol>

        {/* The label that rubbed off is exactly the case the camera cannot
            help with, so typing is never more than one field away. */}
        <form
          className="serial-scan__manual"
          onSubmit={(event) => {
            event.preventDefault();
            void add(typed);
            setTyped('');
          }}
        >
          <input
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            placeholder={t('serials.scan.typed')}
          />
          <button type="submit">{t('common.add')}</button>
        </form>

        <div className="serial-scan__actions">
          <button
            type="button"
            className="primary"
            onClick={() => {
              stop();
              onDone(values);
            }}
          >
            {t('serials.scan.done')}
          </button>
          {!enough && <span className="hint">{t('serials.scan.mismatch')}</span>}
        </div>
      </div>
    </div>,
    document.body,
  );
}
